import React, { useState } from "react";
import {
  Select,
  Popover,
  Form,
  Row,
  Col,
  Typography,
  Button,
  message,
} from "antd";

const { Option } = Select;
const { Text } = Typography;

const frequencyOptions = ["None", "Daily", "Weekly", "Monthly", "Yearly"];
const numberOptions = Array.from({ length: 15 }, (_, i) => i + 1);
const endAfterOptions = Array.from({ length: 5 }, (_, i) => i + 1);

interface RecurringFrequencyPopperProps {
  setRemainders: (value: any) => void;
  disabled?: boolean;
  selectedTaskDetails?: { reminders?: any };
  isSubtaskUpdate?: boolean;
  remainders?: any;
}

const RecurringFrequencyPopper = ({
  setRemainders,
  disabled,
  selectedTaskDetails,
  isSubtaskUpdate,
  remainders,
}: RecurringFrequencyPopperProps) => {
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);

  const handleVisibleChange = (open: boolean) => {
    if (!disabled && !isSubtaskUpdate) {
      setVisible(open);
    }
  };

  const languageConfig: any = {
    Daily: "Days",
    Weekly: "Weeks",
    Monthly: "Months",
    Yearly: "Years",
  };

  const handleSelectClick = () => {
    if (!disabled && !isSubtaskUpdate) {
      setVisible(true);
    }
  };

  // Initialize form with pre-filled values if available
  React.useEffect(() => {
    // Priority: use remainders prop first, then selectedTaskDetails.reminders
    const reminderData = remainders || selectedTaskDetails?.reminders;

    if (reminderData) {
      form.setFieldsValue({
        frequency: reminderData.recurrenceType || undefined,
        onceEvery: reminderData.recurrenceInterval,
        endAfter: reminderData.maxRecurrenceCount,
      });
    } else {
      form.setFieldsValue({
        frequency: undefined,
        onceEvery: undefined,
        endAfter: undefined,
      });
    }
  }, [selectedTaskDetails, remainders, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // If no frequency is selected, don't submit
      if (!values.frequency) {
        message.error("Please select a frequency option.");
        return;
      }

      const formatted = {
        // Always include recurrenceType as backend requires it
        recurrenceType: values.frequency,
        // Only include interval and count if not "None"
        ...(values.frequency !== "None" && {
          recurrenceInterval: values.onceEvery,
          maxRecurrenceCount: values.endAfter,
        }),
        isLoadedFromAPI: false,
      };
      setVisible(false);
      if (setRemainders) {
        setRemainders((prev: any) => {
          // Always merge with formatted object (which now includes recurrenceType)
          // When "None" is selected, it will set recurrenceType to "None"
          const merged = { ...prev, ...formatted };
          return merged;
        });
      }
    } catch (error) {
      message.error("Please fill all fields.");
    }
  };

  // Watch the frequency field for real-time updates
  const frequency = Form.useWatch("frequency", form);
  const isFrequencyNone = frequency === "None";

  const popoverContent = (
    <Form form={form} layout="vertical" disabled={disabled || isSubtaskUpdate}>
      <Form.Item
        name="frequency"
        label="Recurring Frequency"
        rules={[{ required: false, message: "Select frequency" }]}
      >
        <Select
          placeholder="Select Frequency"
          disabled={disabled || isSubtaskUpdate}
          getPopupContainer={(triggerNode) =>
            (triggerNode.parentElement as HTMLElement) || document.body
          }
          onChange={(value) => {
            if (value === "None") {
              form.setFieldsValue({
                onceEvery: undefined,
                endAfter: undefined,
              });
            } else if (value) {
              // When a valid frequency is selected, set default values if they're empty
              const currentOnceEvery = form.getFieldValue("onceEvery");
              const currentEndAfter = form.getFieldValue("endAfter");
              if (!currentOnceEvery) {
                form.setFieldsValue({ onceEvery: 1 });
              }
              if (!currentEndAfter) {
                form.setFieldsValue({ endAfter: 1 });
              }
            }
          }}
        >
          {frequencyOptions.map((freq) => (
            <Option key={freq} value={freq}>
              {freq}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Row gutter={12}>
        <Col span={12}>
          <Form.Item
            name="onceEvery"
            label="Once every"
            rules={[
              {
                required: !isFrequencyNone,
                message: "Required",
              },
            ]}
            dependencies={["frequency"]}
          >
            <Select
              placeholder="Select"
              disabled={disabled || isSubtaskUpdate || isFrequencyNone}
              getPopupContainer={(triggerNode) =>
                (triggerNode.parentElement as HTMLElement) || document.body
              }
            >
              {numberOptions.map((num) => (
                <Option key={num} value={num}>
                  {num}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name="endAfter"
            label="End after"
            rules={[
              {
                required: !isFrequencyNone,
                message: "Required",
              },
            ]}
            dependencies={["frequency"]}
          >
            <Select
              placeholder="Select"
              disabled={disabled || isSubtaskUpdate || isFrequencyNone}
              getPopupContainer={(triggerNode) =>
                (triggerNode.parentElement as HTMLElement) || document.body
              }
            >
              {endAfterOptions.map((num) => (
                <Option key={num} value={num}>
                  {num}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <div style={{ textAlign: "right" }}>
        <Button
          size="small"
          onClick={() => setVisible(false)}
          style={{ marginRight: 8 }}
          disabled={disabled || isSubtaskUpdate}
        >
          Cancel
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={handleSubmit}
          disabled={disabled || isSubtaskUpdate}
        >
          Save
        </Button>
      </div>
    </Form>
  );

  // Get values from form or from remainders state for display
  const onceEvery =
    form.getFieldValue("onceEvery") || remainders?.recurrenceInterval;
  const endAfter =
    form.getFieldValue("endAfter") || remainders?.maxRecurrenceCount;
  const displayFrequency =
    form.getFieldValue("frequency") || remainders?.recurrenceType;

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      open={visible && !(disabled || isSubtaskUpdate)}
      onOpenChange={handleVisibleChange}
      styles={{ body: { width: 280 } }}
      placement="bottomRight"
      arrow={false}
      autoAdjustOverflow
      align={{
        points: ["tr", "br"],
        offset: [-12, 8],
        overflow: { adjustX: 1, adjustY: 1 },
      }}
      getPopupContainer={() => document.body}
    >
      <div
        style={{
          width: "100%",
          height: "38px",
          border: `1px solid #d9d9d9`,
          borderRadius: 4,
          padding: "4px 11px",
          background: disabled || isSubtaskUpdate ? "#f5f5f5" : "#fff",
          display: "flex",
          alignItems: "center",
          cursor: disabled || isSubtaskUpdate ? "not-allowed" : "pointer",
          opacity: disabled || isSubtaskUpdate ? 0.65 : 1,
        }}
        onClick={handleSelectClick}
      >
        {!displayFrequency ? (
          <Text type="secondary">Select Recurring Frequency</Text>
        ) : displayFrequency === "None" ? (
          <Text
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            No Recurrence
          </Text>
        ) : displayFrequency && onceEvery && endAfter ? (
          <Text
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            {`Every ${onceEvery} ${
              onceEvery === 1
                ? languageConfig[displayFrequency]?.replace?.(/s$/, "") || ""
                : languageConfig[displayFrequency] || ""
            }, ends after ${endAfter} ${
              endAfter === 1 ? "occurrence" : "occurrences"
            }`}
          </Text>
        ) : (
          <Text type="secondary">Set Recurring Frequency</Text>
        )}
      </div>
    </Popover>
  );
};

export default RecurringFrequencyPopper;
